import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { costingAPI } from '../../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useConfirm } from '@/components/ConfirmProvider';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, History, Tags, Loader2, Search, X, ArrowUp, ArrowDown, ArrowUpDown, FileSpreadsheet, FileText,
} from 'lucide-react';
import { GREEN, RUST, MUTED, fmtRate, fmtDateTime, downloadBlob, Sparkline } from './helpers';


export const RMMasterSection = ({ refreshKey }) => {
  const confirm = useConfirm();
  const [categories, setCategories] = useState([]);
  const [rms, setRms] = useState([]);
  const [trends, setTrends] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('ALL');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [editingRateId, setEditingRateId] = useState(null);
  const [rateDraft, setRateDraft] = useState('');
  const movedRef = useRef(false);


  const [rmDialog, setRmDialog] = useState(false);
  const [editingRm, setEditingRm] = useState(null);
  const [rmForm, setRmForm] = useState({ name: '', category: '', rate_per_kg: '' });
  const [savingRm, setSavingRm] = useState(false);


  const [catDialog, setCatDialog] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [catBusy, setCatBusy] = useState(false);


  const [histDialog, setHistDialog] = useState(false);
  const [histRm, setHistRm] = useState(null);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, rmRes, trendRes] = await Promise.all([
        costingAPI.listCategories(),
        costingAPI.listRawMaterials(),
        costingAPI.rateTrends(),
      ]);
      setCategories(catRes.data || []);
      setRms(rmRes.data || []);
      setTrends(trendRes.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load costing data');
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => { load(); }, [load, refreshKey]);


  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rms.filter((r) => {
      if (catFilter !== 'ALL' && r.category !== catFilter) return false;
      if (q && !(`${r.name}`.toLowerCase().includes(q) || `${r.category}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rms, search, catFilter]);


  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return filtered.slice().sort((a, b) => {
      if (sortKey === 'rate_per_kg') return (Number(a.rate_per_kg || 0) - Number(b.rate_per_kg || 0)) * dir;
      const av = `${a[sortKey] || ''}`.toLowerCase();
      const bv = `${b[sortKey] || ''}`.toLowerCase();
      return av.localeCompare(bv) * dir;
    });
  }, [filtered, sortKey, sortDir]);


  const totalAvg = useMemo(() => {
    if (!rms.length) return 0;
    return rms.reduce((a, r) => a + Number(r.rate_per_kg || 0), 0) / rms.length;
  }, [rms]);


  const openAddRm = () => {
    setEditingRm(null);
    setRmForm({ name: '', category: categories[0]?.name || '', rate_per_kg: '' });
    setRmDialog(true);
  };
  const openEditRm = (rm) => {
    setEditingRm(rm);
    setRmForm({ name: rm.name, category: rm.category, rate_per_kg: String(rm.rate_per_kg ?? '') });
    setRmDialog(true);
  };
  const saveRm = async () => {
    const name = rmForm.name.trim();
    const category = rmForm.category.trim();
    const rate = parseFloat(rmForm.rate_per_kg);
    if (!name) { toast.error('Name is required'); return; }
    if (!category) { toast.error('Category is required'); return; }
    if (isNaN(rate) || rate < 0) { toast.error('Enter a valid rate (₹/kg)'); return; }
    setSavingRm(true);
    try {
      if (editingRm) {
        await costingAPI.updateRawMaterial(editingRm.id, { name, category, rate_per_kg: rate });
        toast.success('Raw material updated');
      } else {
        await costingAPI.createRawMaterial({ name, category, rate_per_kg: rate });
        toast.success('Raw material added');
      }
      setRmDialog(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingRm(false);
    }
  };
  const deleteRm = async (rm) => {
    const ok = await confirm({
      title: 'Delete raw material?',
      description: `"${rm.name}" and its rate history will be permanently removed.`,
      confirmLabel: 'Delete', destructive: true,
    });
    if (!ok) return;
    try {
      await costingAPI.deleteRawMaterial(rm.id);
      setRms((prev) => prev.filter((r) => r.id !== rm.id));
      toast.success('Raw material deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };


  const addCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    setCatBusy(true);
    try {
      await costingAPI.createCategory(name);
      setNewCat('');
      const res = await costingAPI.listCategories();
      setCategories(res.data || []);
      toast.success('Category added');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    } finally { setCatBusy(false); }
  };
  const saveCategoryEdit = async (cat) => {
    const name = editingCatName.trim();
    if (!name) return;
    setCatBusy(true);
    try {
      await costingAPI.updateCategory(cat.id, name);
      setEditingCatId(null);
      setEditingCatName('');
      await load();
      toast.success('Category renamed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
    } finally { setCatBusy(false); }
  };
  const deleteCategory = async (cat) => {
    const ok = await confirm({
      title: 'Delete category?',
      description: `"${cat.name}" will be removed. Raw materials using it must be reassigned first.`,
      confirmLabel: 'Delete', destructive: true,
    });
    if (!ok) return;
    setCatBusy(true);
    try {
      await costingAPI.deleteCategory(cat.id);
      setCategories((prev) => prev.filter((c) => c.id !== cat.id));
      toast.success('Category deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setCatBusy(false); }
  };


  const beginRateEdit = (r) => { setEditingRateId(r.id); setRateDraft(String(r.rate_per_kg ?? '')); };


  const saveRateIfChanged = async (r) => {
    const val = parseFloat(rateDraft);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid rate (₹/kg)'); return false; }
    const live = rms.find((x) => x.id === r.id);
    if (live && val !== Number(live.rate_per_kg)) {
      try {
        await costingAPI.updateRawMaterial(r.id, { rate_per_kg: val });
        setRms((prev) => prev.map((x) => (x.id === r.id ? { ...x, rate_per_kg: val } : x)));
        setTrends((prev) => ({ ...prev, [r.id]: [...(prev[r.id] || []), { changed_at: new Date().toISOString(), rate: val }] }));
        toast.success('Rate updated');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Update failed');
        return false;
      }
    }
    return true;
  };


  const commitRateAndMove = async (r, i) => {
    const nextRm = sorted[i + 1];
    const ok = await saveRateIfChanged(r);
    if (!ok) { movedRef.current = false; return; }
    if (nextRm) { setEditingRateId(nextRm.id); setRateDraft(String(nextRm.rate_per_kg ?? '')); }
    else setEditingRateId(null);
  };


  const commitRateOnly = async (r) => {
    if (movedRef.current) { movedRef.current = false; return; }
    await saveRateIfChanged(r);
    setEditingRateId(null);
  };


  const openHistory = async (rm) => {
    setHistRm(rm);
    setHistDialog(true);
    setHistLoading(true);
    try {
      const res = await costingAPI.rateHistory(rm.id);
      setHistory(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load history');
    } finally { setHistLoading(false); }
  };


  const [exporting, setExporting] = useState(false);
  const exportMaster = async (kind) => {
    setExporting(true);
    try {
      const res = kind === 'excel' ? await costingAPI.exportRmMasterExcel() : await costingAPI.exportRmMasterPdf();
      downloadBlob(res, `rm_master.${kind === 'excel' ? 'xlsx' : 'pdf'}`);
    } catch (e) {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };


  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  const SortHead = ({ label, keyName, align = 'left' }) => (
    <th
      className={`px-4 py-2.5 font-semibold cursor-pointer select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => toggleSort(keyName)}
      data-testid={`costing-sort-${keyName}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {sortKey === keyName
          ? (sortDir === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)
          : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
      </span>
    </th>
  );


  return (
    <div>
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <Card className="p-4" data-testid="costing-summary-rms">
          <p className="text-xs uppercase tracking-wide" style={{ color: MUTED }}>Raw Materials</p>
          <p className="text-2xl font-bold" style={{ color: GREEN }}>{rms.length}</p>
        </Card>
        <Card className="p-4" data-testid="costing-summary-categories">
          <p className="text-xs uppercase tracking-wide" style={{ color: MUTED }}>Categories</p>
          <p className="text-2xl font-bold" style={{ color: GREEN }}>{categories.length}</p>
        </Card>
        <Card className="p-4" data-testid="costing-summary-avg">
          <p className="text-xs uppercase tracking-wide" style={{ color: MUTED }}>Avg Rate / kg</p>
          <p className="text-2xl font-bold" style={{ color: RUST }}>{fmtRate(totalAvg)}</p>
        </Card>
      </div>


      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
          <Input data-testid="costing-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search material / category" className="pl-8 w-56 h-9" />
          {search && (
            <button data-testid="costing-search-clear" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4" style={{ color: MUTED }} />
            </button>
          )}
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-48 h-9" data-testid="costing-category-filter">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All categories</SelectItem>
            {categories.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="outline" className="h-9" onClick={() => exportMaster('excel')} disabled={exporting} data-testid="costing-export-excel">
          <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel
        </Button>
        <Button variant="outline" className="h-9" onClick={() => exportMaster('pdf')} disabled={exporting} data-testid="costing-export-pdf">
          <FileText className="w-4 h-4 mr-1.5" /> PDF
        </Button>
        <Button variant="outline" className="h-9" onClick={() => setCatDialog(true)} data-testid="costing-manage-categories-btn">
          <Tags className="w-4 h-4 mr-1.5" /> Categories
        </Button>
        <Button className="h-9 text-white" style={{ background: GREEN }} onClick={openAddRm} data-testid="costing-add-rm-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Raw Material
        </Button>
      </div>


      {/* Table */}
      {loading ? (
        <div className="flex items-center gap-2 py-16 justify-center" style={{ color: MUTED }}>
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center" style={{ color: MUTED }} data-testid="costing-empty">
          {rms.length === 0 ? 'No raw materials yet. Click "Add Raw Material" to begin.' : 'No raw materials match your filters.'}
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: '#FBF9F6', color: MUTED }} className="text-xs uppercase tracking-wide">
                <SortHead label="Name" keyName="name" />
                <SortHead label="Category" keyName="category" />
                <th className="px-4 py-2.5 font-semibold text-left">Trend</th>
                <SortHead label="Rate / kg" keyName="rate_per_kg" align="right" />
                <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr key={r.id} className="border-t hover:bg-[#FBF9F6]" style={{ borderColor: '#F0EEE9' }} data-testid="costing-rm-row">
                  <td className="px-4 py-2.5 font-medium" style={{ color: '#1E2420' }}>{r.name}</td>
                  <td className="px-4 py-2.5" style={{ color: MUTED }}>{r.category}</td>
                  <td className="px-4 py-2.5"><Sparkline values={(trends[r.id] || []).map((p) => p.rate)} /></td>
                  <td className="px-4 py-2.5 text-right">
                    {editingRateId === r.id ? (
                      <input
                        autoFocus
                        type="number"
                        step="0.01"
                        min="0"
                        inputMode="decimal"
                        value={rateDraft}
                        onChange={(e) => setRateDraft(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); movedRef.current = true; commitRateAndMove(r, i); }
                          else if (e.key === 'Escape') { movedRef.current = true; setEditingRateId(null); }
                        }}
                        onBlur={() => commitRateOnly(r)}
                        className="w-24 h-8 px-2 text-right tabular-nums border rounded outline-none focus:ring-2"
                        style={{ borderColor: GREEN, color: GREEN }}
                        data-testid="costing-rate-input"
                      />
                    ) : (
                      <button
                        onClick={() => beginRateEdit(r)}
                        title="Click to edit rate"
                        className="font-semibold tabular-nums hover:underline decoration-dotted underline-offset-4 cursor-pointer"
                        style={{ color: GREEN }}
                        data-testid="costing-rate-cell"
                      >
                        {fmtRate(r.rate_per_kg)}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Rate history" onClick={() => openHistory(r)} data-testid="costing-history-btn">
                        <History className="w-4 h-4" style={{ color: MUTED }} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Edit" onClick={() => openEditRm(r)} data-testid="costing-edit-btn">
                        <Pencil className="w-4 h-4" style={{ color: GREEN }} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Delete" onClick={() => deleteRm(r)} data-testid="costing-delete-btn">
                        <Trash2 className="w-4 h-4" style={{ color: RUST }} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2" style={{ background: '#FBF9F6', borderColor: GREEN }}>
                <td className="px-4 py-2.5 font-bold" style={{ color: GREEN }} colSpan={3}>TOTAL · {sorted.length} material(s)</td>
                <td className="px-4 py-2.5 text-right font-bold" style={{ color: GREEN }}>avg {fmtRate(sorted.length ? sorted.reduce((a, r) => a + Number(r.rate_per_kg || 0), 0) / sorted.length : 0)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </Card>
      )}


      {/* Add / Edit RM dialog */}
      <Dialog open={rmDialog} onOpenChange={setRmDialog}>
        <DialogContent data-testid="costing-rm-dialog">
          <DialogHeader>
            <DialogTitle>{editingRm ? 'Edit Raw Material' : 'Add Raw Material'}</DialogTitle>
            <DialogDescription>Only Name, Category and Rate (₹/kg) are tracked. Rate changes are logged automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium" style={{ color: MUTED }}>Name</label>
              <Input data-testid="costing-rm-name" value={rmForm.name} onChange={(e) => setRmForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Maize" />
            </div>
            <div>
              <label className="text-xs font-medium" style={{ color: MUTED }}>Category</label>
              <Select value={rmForm.category} onValueChange={(v) => setRmForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="costing-rm-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium" style={{ color: MUTED }}>Rate (₹ / kg)</label>
              <Input data-testid="costing-rm-rate" type="number" step="0.01" min="0" inputMode="decimal" value={rmForm.rate_per_kg} onChange={(e) => setRmForm((f) => ({ ...f, rate_per_kg: e.target.value }))} placeholder="0.00" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRmDialog(false)}>Cancel</Button>
            <Button className="text-white" style={{ background: GREEN }} onClick={saveRm} disabled={savingRm} data-testid="costing-rm-save">
              {savingRm ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              {editingRm ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Manage Categories dialog */}
      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent data-testid="costing-category-dialog">
          <DialogHeader>
            <DialogTitle>Manage Categories</DialogTitle>
            <DialogDescription>Add, rename or remove raw-material categories.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mb-2">
            <Input data-testid="costing-new-category" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }} />
            <Button className="text-white" style={{ background: GREEN }} onClick={addCategory} disabled={catBusy || !newCat.trim()} data-testid="costing-add-category-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y" style={{ borderColor: '#F0EEE9' }}>
            {categories.map((c) => (
              <div key={c.id} className="flex items-center gap-2 py-2" data-testid="costing-category-row">
                {editingCatId === c.id ? (
                  <>
                    <Input value={editingCatName} onChange={(e) => setEditingCatName(e.target.value)} className="h-8" onKeyDown={(e) => { if (e.key === 'Enter') saveCategoryEdit(c); }} />
                    <Button size="sm" className="h-8 text-white" style={{ background: GREEN }} onClick={() => saveCategoryEdit(c)} disabled={catBusy}>Save</Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => { setEditingCatId(null); setEditingCatName(''); }}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 font-medium" style={{ color: '#1E2420' }}>{c.name}</span>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditingCatId(c.id); setEditingCatName(c.name); }} data-testid="costing-category-edit">
                      <Pencil className="w-4 h-4" style={{ color: GREEN }} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => deleteCategory(c)} data-testid="costing-category-delete">
                      <Trash2 className="w-4 h-4" style={{ color: RUST }} />
                    </Button>
                  </>
                )}
              </div>
            ))}
            {categories.length === 0 && <p className="py-4 text-center text-sm" style={{ color: MUTED }}>No categories yet.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Rate history dialog */}
      <Dialog open={histDialog} onOpenChange={setHistDialog}>
        <DialogContent data-testid="costing-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5" style={{ color: GREEN }} /> Rate History</DialogTitle>
            <DialogDescription>{histRm?.name}</DialogDescription>
          </DialogHeader>
          {histLoading ? (
            <div className="flex items-center gap-2 py-8 justify-center" style={{ color: MUTED }}><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
          ) : history.length === 0 ? (
            <p className="py-6 text-center text-sm" style={{ color: MUTED }}>No rate changes recorded.</p>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              <div className="mb-3"><Sparkline values={history.slice().reverse().map((h) => h.new_rate)} width={240} height={48} /></div>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#FBF9F6', color: MUTED }} className="text-left text-xs uppercase">
                    <th className="px-3 py-2">When</th><th className="px-3 py-2">Old</th><th className="px-3 py-2">New</th><th className="px-3 py-2">By</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-t" style={{ borderColor: '#F0EEE9' }} data-testid="costing-history-row">
                      <td className="px-3 py-2 whitespace-nowrap" style={{ color: MUTED }}>{fmtDateTime(h.changed_at)}</td>
                      <td className="px-3 py-2 tabular-nums" style={{ color: MUTED }}>{h.old_rate == null ? '—' : fmtRate(h.old_rate)}</td>
                      <td className="px-3 py-2 tabular-nums font-semibold" style={{ color: GREEN }}>{fmtRate(h.new_rate)}</td>
                      <td className="px-3 py-2" style={{ color: MUTED }}>{h.changed_by_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
